import { useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { observer, inject } from "mobx-react";
import Input from "../form-fields/Input";
import ButtonWithLoading from '../form-fields/ButtonWithLoading';
import RateLimitError from '../errors/RateLimitError';
import Link from '@/components/common/Link';
import useRegisterSchema from '@/hooks/useRegisterSchema';

const Register = inject('studioStore')(observer(({ studioStore }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isRateLimited, setIsRateLimited] = useState(false);
  const router = useRouter();
  const tg = useTranslations('General');
  const t = useTranslations('Account');
  const container = studioStore.settings.login_register?.container;
  const containerBackgroundColor = studioStore.settings.login_register?.container_background_color;

  let classes = "login-register-content max-w-lg mx-auto px-6 py-7 overflow-hidden mt-8";
  const style = {
    backgroundColor: containerBackgroundColor,
  };

  if (container === "stamp") {
    classes = classes + " stamp-border";
    style.backgroundImage = `radial-gradient(8px, transparent 98%, ${containerBackgroundColor}),linear-gradient(${containerBackgroundColor} 0px, ${containerBackgroundColor} 0px)`;
    style.backgroundColor = '';
  }

  const schema = useRegisterSchema();

  const validateForm = async () => {
    try {
      await schema.validate({
        email,
        password,
        repeat_password: repeatPassword,
        first_name: firstName,
        last_name: lastName,
      }, { abortEarly: false });
      return true;
    } catch (validationError) {
      const newErrors = {};
      if (validationError.inner && validationError.inner.length > 0) {
        validationError.inner.forEach((err) => {
          newErrors[err.path] = err.message;
        });
      } else {
        newErrors[validationError.path] = validationError.message;
      }
      setErrors(newErrors);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(await validateForm())) return;

    setIsLoading(true);
    setIsRateLimited(false);
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          repeat_password: repeatPassword,
          first_name: firstName,
          last_name: lastName,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          // Save token and user data
          localStorage.setItem('token', data.token);
          localStorage.setItem('userData', JSON.stringify(data.user));

          // Handle any redirect URL
          const redirectUrl = localStorage.getItem('redirectUrl');
          localStorage.removeItem('redirectUrl');
          router.push(redirectUrl || '/');
        } else {
          setErrors({ general: t('error_no_token') });
          setIsLoading(false);
        }
      } else {
        const errorData = await response.json();
        if (response.status === 429) {
          setIsRateLimited(true);
        } else {
          setErrors({ general: errorData.message || t('error_generic') });
        }
        setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
      setErrors({ general: t('error_generic') });
      setIsLoading(false);
    }
  };

  return (
    <div className={`${classes}`} style={style}>
      <div className="content p-5">
        <h2 className="text-2xl uppercase font-medium mb-1">{t('register')}</h2>
        {isRateLimited && <div className="mb-4"><RateLimitError /></div>}
        {errors.general && !isRateLimited && <p className="text-red-600 text-sm mb-4">{errors.general}</p>}
        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="space-y-2">
            <Input
              title={t('first_name')}
              type="text"
              name="firstName"
              value={firstName}
              handleChange={(e) => setFirstName(e.target.value)}
              required
              data-testid="register-firstname"
            />
            {errors.firstName && <p className="text-red-600 text-sm">{errors.firstName}</p>}

            <Input
              title={t('last_name')}
              type="text"
              name="lastName"
              value={lastName}
              handleChange={(e) => setLastName(e.target.value)}
              required
              data-testid="register-lastname"
            />
            {errors.lastName && <p className="text-red-600 text-sm">{errors.lastName}</p>}

            <Input
              title={t('email')}
              type="email"
              name="email"
              value={email}
              handleChange={(e) => setEmail(e.target.value)}
              required
              data-testid="register-email"
            />
            {errors.email && <p className="text-red-600 text-sm">{errors.email}</p>}

            <Input
              title={t('password')}
              type="password"
              name="password"
              value={password}
              handleChange={(e) => setPassword(e.target.value)}
              required
              data-testid="register-password"
            />
            {errors.password && <p className="text-red-600 text-sm">{errors.password}</p>}

            <Input
              title={t('repeat_password')}
              type="password"
              name="repeatPassword"
              value={repeatPassword}
              handleChange={(e) => setRepeatPassword(e.target.value)}
              required
              data-testid="register-repeat-password"
            />
            {errors.repeatPassword && <p className="text-red-600 text-sm">{errors.repeatPassword}</p>}
          </div>
          <div className="mt-4">
            <ButtonWithLoading
              isLoading={isLoading}
              text={t('register')}
              loadingText={tg('loading')}
              type="submit"
              data-testid="register-submit"
            />
            <div className="flex gap-2 pt-5">
              <p className="text-gray-600 text-sm">{t('already_have_account')}</p>
              <Link className="text-gray-600 text-sm underline" href="/login">
                {t('login_here')}
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}));

export default Register;
